import React, { useState } from 'react';
import { Card, Typography, Modal, Button, Table } from "antd";
import { ExpandOutlined, CloseOutlined, TableOutlined, BarChartOutlined } from '@ant-design/icons';
import { LineChart } from '@mui/x-charts/LineChart';

const { Title } = Typography;

export interface MetricDataPoint {
    date: string;
    value: number;
}

export interface MetricDataset {
    name: string;
    data: MetricDataPoint[];
    color?: string;
}

interface DashboardChartProps {
    title: string;
    datasets: MetricDataset[];
    height?: number | string;
}

const DashboardChart: React.FC<DashboardChartProps> = ({ title, datasets, height = 400 }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showGrid, setShowGrid] = useState(false);

    const handleExpand = () => {
        setIsModalOpen(true);
        setShowGrid(false);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setShowGrid(false);
    };

    const toggleView = () => {
        setShowGrid(!showGrid);
    };

    if (datasets.length === 0 || datasets[0].data.length === 0) {
        return (
            <Card style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                    <Title level={5} style={{ margin: 0 }}>{title}</Title>
                    <Button 
                        type="text" 
                        icon={<ExpandOutlined />} 
                        onClick={handleExpand}
                        title="Expandir gráfico"
                    />
                </div>
                <div style={{ 
                    flex: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    minHeight: 0
                }}>
                    <p>Sin información</p>
                </div>
            </Card>
        );
    }

    const allValues = datasets.flatMap(dataset => 
        dataset.data.map(point => point.value)
    );
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    
    const calculateYAxisBounds = (min: number, max: number): { min: number, max: number } => {
        if (min === 0 && max === 0) {
            return { min: -10, max: 10 };
        }
        
        if (min === max) {
            if (min >= 0) {
                return { min: 0, max: min * 1.1 };
            } else {
                return { min: min * 1.1, max: 0 };
            }
        }
        
        if (min >= 0) {
            return { 
                min: 0, 
                max: max * 1.05
            };
        }
        
        if (max <= 0) {
            return { 
                min: min * 1.05,
                max: 0 
            };
        }
        
        const range = max - min;
        const margin = range * 0.05;
        
        return {
            min: min - margin,
            max: max + margin
        };
    };

    const { min: yMin, max: yMax } = calculateYAxisBounds(minValue, maxValue);
    
    const calculateYTicks = (min: number, max: number): number[] => {
        const range = max - min;
        
        if (range === 0) {
            return [min - 5, min, min + 5];
        }
        
        const magnitude = Math.pow(10, Math.floor(Math.log10(range)));
        let step = magnitude;
        
        if (range / step > 10) {
            step = step * 2;
        } else if (range / step < 4) {
            step = step / 2;
        }
        
        const start = Math.floor(min / step) * step;
        const end = Math.ceil(max / step) * step;
        const numberOfTicks = Math.min(Math.max(Math.floor((end - start) / step) + 1, 3), 8);
        
        return Array.from({ length: numberOfTicks }, (_, i) => {
            const tickValue = start + (i * step);
            return Math.round(tickValue * 100) / 100;
        });
    };

    const yAxisTicks = calculateYTicks(yMin, yMax);

    const series = datasets.map(dataset => ({
        data: dataset.data.map(point => point.value),
        label: dataset.name,
        color: dataset.color,
    }));

    const xAxisData = datasets[0].data.map(point => point.date);

    const tableColumns = [
        {
            title: 'Fecha',
            dataIndex: 'date',
            key: 'date',
            fixed: 'left' as const,
            width: 120,
        },
        ...datasets.map(dataset => ({
            title: dataset.name,
            dataIndex: dataset.name,
            key: dataset.name,
            render: (value: number) => value?.toLocaleString() || '0',
        })),
    ];

    const tableData = xAxisData.map((date, index) => {
        const row: any = { key: index, date };
        datasets.forEach(dataset => {
            row[dataset.name] = dataset.data[index]?.value || 0;
        });
        return row;
    });

    const renderChart = (chartHeight: number | string) => (
        <LineChart
            series={series.map((s, index) => ({
                data: s.data,
                label: s.label,
                curve: 'linear',
                color: s.color,
            }))}
            xAxis={[{ 
                data: xAxisData, 
                scaleType: 'point',
            }]}
            yAxis={[{
                min: yMin,
                max: yMax,
                tickInterval: yAxisTicks,
            }]}
            grid={{ vertical: true, horizontal: true }}
            height={typeof chartHeight === 'number' ? chartHeight : 400}
            slotProps={{
                legend: {
                    position: { 
                        vertical: 'top', 
                    },
                },
            }}
        />
    );

    const renderGrid = () => (
        <Table
            columns={tableColumns}
            dataSource={tableData}
            scroll={{ x: true, y: 'calc(100vh - 200px)' }}
            pagination={false}
            size="small"
            style={{ width: '100%', height: '100%' }}
        />
    );

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Card style={{ height: '100%', display: 'flex', flexDirection: 'column' }} bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '12px' }}>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    marginBottom: 8,
                    flexShrink: 0 
                }}>
                    <Title level={5} style={{ margin: 0 }}>{title}</Title>
                    <Button 
                        type="text" 
                        icon={<ExpandOutlined />} 
                        onClick={handleExpand}
                        title="Expandir gráfico"
                    />
                </div>
                <div style={{ 
                    flex: 1, 
                    width: '100%', 
                    minHeight: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    {renderChart('100%')}
                </div>
            </Card>

            <Modal
                title={
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        width: '100%'
                    }}>
                        <span>{title}</span>
                        <Button 
                            type="text" 
                            icon={showGrid ? <BarChartOutlined /> : <TableOutlined />}
                            onClick={toggleView}
                        >
                            {showGrid ? 'Ver Gráfico' : 'Ver Grilla'}
                        </Button>
                    </div>
                }
                open={isModalOpen}
                onCancel={handleCloseModal}
                footer={null}
                width="95vw"
                style={{ top: 20 }}
                bodyStyle={{ 
                    height: 'calc(100vh - 100px)', 
                    padding: '16px 0',
                    overflow: 'hidden'
                }}
                closeIcon={<CloseOutlined />}
            >
                <div style={{ 
                    height: '100%', 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {showGrid ? renderGrid() : renderChart('100%')}
                </div>
            </Modal>
        </div>
    );
};

export default DashboardChart;